import {
    unwrapResponse,
    makeApiCall,
    unwrapResponseWithCode,
    ApiCallParams,
    makeDownstreamApiCall,
} from "@payment-client/utils/Api";
import * as WalletApi from "@payment-client/interfaces/WalletApi";
import { VALID_WALLET_LINK_STATUSES } from "@swiggy-private/payment-core";
import { WALLET_CODES } from "@swiggy-private/payment-types";
import { ApiResponse } from "@payment-client/interfaces/Api";
import { DOWNSTREAM_ENDPOINTS, DOWNSTREAM_HOSTS } from "@payment-client/constants/Hosts";

const ENDPOINT = {
    GET_WALLET_BALANCE: "/api/v1/get-wallet-balance",
    ADD_MONEY: "/api/v1/add-money-wallet",
    LINK_WALLET: "/api/v1/link-wallet",
    VALIDATE_OTP: "/api/v1/validate-link-wallet",
    APAY_SDK_SIGN_AND_ENCRPYT: "/api/v1/amazonpay/sign-and-encrypt",
    APAY_SDK_VALIDATE_TRANSACTION: "/api/v1/amazonpay/validate-transaction",
    DELINK_WALLET: "/api/v1/delink-wallet",
};

interface AddMoneyResponseData {
    url: string;
    params: { [key in string]: string };
}

export async function linkWallet(
    apiHost: string,
    walletCode: WALLET_CODES,
    mobileNumber?: string,
): Promise<object | null> {
    const searchParams: ApiCallParams["searchParams"] = { walletCode };

    if (mobileNumber != null) {
        searchParams.mobileNumber = mobileNumber;
    }

    const response = await makeApiCall<object | null>({
        apiHost,
        endpoint: ENDPOINT.LINK_WALLET,
        searchParams,
        options: {
            method: "POST",
        },
    });

    if (response.statusCode === 0) {
        return response.data || {};
    }

    throw response;
}

export async function validateOtp(
    apiHost: string,
    params: { walletCode: string; otp: string; otpId?: string; state?: string },
): Promise<WalletApi.VerifyOtpResponse> {
    const { walletCode, ...bodyParams } = params;
    const response = unwrapResponseWithCode(
        await makeApiCall<WalletApi.VerifyOtpResponse>({
            apiHost,
            endpoint: ENDPOINT.VALIDATE_OTP,
            searchParams: { walletCode },
            bodyParams: { ...bodyParams },
            options: {
                method: "POST",
            },
        }),
    );

    return response.toPromise();
}

export async function getWalletBalance(
    apiHost: string,
    walletCode: string,
): Promise<WalletApi.GetWalletBalanceResponse> {
    const response = unwrapResponse(
        await makeApiCall<WalletApi.GetWalletBalanceResponse>({
            apiHost,
            endpoint: ENDPOINT.GET_WALLET_BALANCE,
            searchParams: { walletCode },
            options: {},
        }),
    );

    return response.unwrapOrElse(() => ({
        linkStatus: VALID_WALLET_LINK_STATUSES.RETRY,
        balance: 0,
        isOldWalletLinkFlow: false,
        mobileNumber: "",
    }));
}

export async function addMoney(apiHost: string, params: { [key: string]: unknown }): Promise<AddMoneyResponseData> {
    const response = unwrapResponse(
        await makeApiCall<AddMoneyResponseData>({
            apiHost,
            endpoint: ENDPOINT.ADD_MONEY,
            bodyParams: params,
            options: {
                method: "POST",
            },
        }),
    );

    return response.toPromise();
}

export async function delinkWallet(apiHost: string, walletCode: string): Promise<AddMoneyResponseData> {
    const response = unwrapResponse(
        await makeApiCall<AddMoneyResponseData>({
            apiHost,
            endpoint: ENDPOINT.DELINK_WALLET,
            searchParams: { walletCode },
            options: {
                method: "POST",
            },
            shouldShowNetworkError: false,
        }),
    );

    return response.toPromise();
}

export async function aPaySignAndEncrypt(
    apiHost: string,
    params: { [key: string]: unknown },
): Promise<WalletApi.APaySignAndEncrypt> {
    const response = unwrapResponse(
        await makeApiCall<WalletApi.APaySignAndEncrypt>({
            apiHost,
            endpoint: ENDPOINT.APAY_SDK_SIGN_AND_ENCRPYT,
            bodyParams: params,
            options: {
                method: "POST",
            },
        }),
    );

    return response.toPromise();
}

export async function aPayValidateTransaction(
    apiHost: string,
    params: { [key: string]: unknown },
): Promise<WalletApi.APayValidateTransaction> {
    const response = unwrapResponse(
        await makeApiCall<WalletApi.APayValidateTransaction>({
            apiHost,
            endpoint: ENDPOINT.APAY_SDK_VALIDATE_TRANSACTION,
            bodyParams: params,
            options: {
                method: "POST",
            },
        }),
    );

    return response.toPromise();
}

export async function aPayValidateTransactionPublic(params: {
    [key: string]: unknown;
}): Promise<ApiResponse<WalletApi.APayValidateTransaction>> {
    const response = await makeDownstreamApiCall<ApiResponse<WalletApi.APayValidateTransaction>>({
        apiHost: DOWNSTREAM_HOSTS.PAYMENT_PRESENTATION_SERVICE_HOST,
        endpoint: DOWNSTREAM_ENDPOINTS.APAY_PUBLIC_VALIDATE_TRANSACTION,
        bodyParams: params,
        options: {
            method: "POST",
        },
    });

    return response;
}
